/**
 * Business Portal Navigation Configuration
 *
 * Defines the sidebar structure, tabs, and settings hub for the Business Portal.
 *
 * @see Issue #330 - Unified Navigation System
 * @see Issue #428 - Route-level RBAC for portal navigation
 */

import { Permission } from '../../enums/permissions';
import type { SettingsHubConfig, SidebarConfig } from '../types';

/**
 * Business Portal Sidebar Configuration
 *
 * Consolidated rail (post Documents-section retirement, post Finance split):
 * 1. Home (Dashboard, Inbox + Tasks + E-Sign Queue, Astor, Watchlist)
 * 2. Relations (unified CRM + Partners)
 * 3. Prospects (Discovery, Leads, Sequences, Email, Voice)
 * 4. Loans (Applications, Term Sheets, Cases)
 * 5. Finance (Payments, Funds, Offerings) — operational money flows; gated by
 *    `billing.read`. Distinct from Reports because these manage real payouts /
 *    capital surfaces, not analytics.
 * 6. Reports (Scorecard, Pipeline Analytics, Loan Portfolio, Financial,
 *    E-Signature History, Retained Copies, Document Approvals) — analytics +
 *    historical/audit surfaces; gated by `analytics.read`.
 * 7. Workspace (Team, Tools)
 *
 * Notes on the documents/e-sign restructure:
 * - The standalone `Documents` rail item was removed. Document uploads happen
 *   contextually inside loans (data room) and entity detail pages.
 * - Active e-signature chasing (pending / sent / draft envelopes that need
 *   action) lives at /inbox/e-signatures next to Tasks — "needs my attention"
 *   semantics.
 * - Historical e-signatures (completed / voided) and Retained Copies are
 *   under Reports — read-only audit surfaces.
 * - The orphan-upload + cross-loan approval queue from /documents was
 *   replaced by Reports › Document Approvals (no upload form).
 * - Document templates continue to live in Settings Hub.
 */
export const BUSINESS_PORTAL_NAV_CONFIG: SidebarConfig = {
  portalId: 'business',
  portalName: 'Business Portal',
  width: 240,
  storageKey: 'business-sidebar-state',
  layoutMode: 'rail',

  standaloneItems: [],

  sections: [
    // ── Home: Dashboard + Inbox + Tasks + Astor ──
    {
      id: 'home',
      label: 'navigation.home',
      icon: 'Home',
      // Home is the only section that doesn't follow the "rail click → first
      // alphabetical item" rule — clicking the Home rail icon should always
      // land on the dashboard, even though "Astor" sorts above "Home"
      // alphabetically in the secondary panel.
      defaultItemId: 'dashboard',
      items: [
        {
          id: 'dashboard',
          label: 'navigation.home',
          path: '/dashboard',
          icon: 'Home',
          keywords: ['dashboard', 'overview', 'home'],
        },
        {
          id: 'inbox',
          label: 'navigation.inbox',
          path: '/inbox',
          icon: 'Inbox',
          keywords: [
            'messages',
            'inbox',
            'mail',
            'communication',
            'notifications',
            'tasks',
            'todos',
            'assignments',
            'esign',
            'e-signature',
            'signing',
            'queue',
          ],
          tabs: [
            { id: 'inbox', label: 'navigation.inbox', path: '/inbox', icon: 'Inbox' },
            { id: 'tasks', label: 'navigation.tasks', path: '/tasks', icon: 'Assignment' },
            // BIC approver dashboard (Issue #1559 — Stream E / Phase 4). Surfaces
            // pending approval requests for the caller as approver AND their own
            // pending requests. Visible to ANY authed user (any role can be a
            // requestor; manager/principal roles are also approvers).
            { id: 'approvals', label: 'navigation.approvals', path: '/approvals', icon: 'Gavel' },
            // Active e-signature chasing (pending/sent/draft envelopes). Historical
            // completed/voided envelopes live under Reports › E-Signature History.
            { id: 'esign-queue', label: 'navigation.eSignQueue', path: '/inbox/e-signatures', icon: 'Draw' },
          ],
        },
        {
          id: 'astor',
          label: 'navigation.astor',
          path: '/astor',
          icon: 'AutoAwesome',
          keywords: ['astor', 'ai', 'assistant', 'agent', 'chat'],
          lock: { anyPermission: [Permission.agents.read] },
        },
        {
          id: 'watchlist',
          label: 'navigation.watchlist',
          path: '/watchlist',
          // Matches the home Watchlist card icon (`PageviewIcon` in
          // `command-center/Watchlist.tsx`) so both surfaces use the same glyph.
          icon: 'Pageview',
          keywords: ['watchlist', 'pinned', 'pins', 'bookmarks', 'favorites', 'starred'],
        },
      ],
    },

    // ── Relations: People Hub (CRM + Borrowers + Borrowing Entities + Guarantors + Partners) ──
    {
      id: 'contacts',
      label: 'navigation.contacts',
      icon: 'Contacts',
      items: [
        {
          id: 'contacts',
          label: 'navigation.contacts',
          path: '/contacts',
          icon: 'Contacts',
          keywords: [
            'contacts',
            'borrowers',
            'partners',
            'investors',
            'crm',
            'people',
            'entities',
            'guarantors',
            'referrals',
            'b2b',
            'affiliates',
            'agreements',
            'commissions',
          ],
          tabs: [
            // Directory is the unified contact list — acts as Contacts' overview.
            // Intentionally section-less so the ungrouped-first sort rule pins
            // it to the top of the panel (same pattern as Control Center Overview).
            { id: 'directory', label: 'navigation.contactDirectory', path: '/contacts', icon: 'Contacts' },
            // Borrowers group — the people we lend to.
            {
              id: 'borrowers',
              label: 'navigation.borrowers',
              path: '/loans/borrowers',
              icon: 'People',
              section: 'navigation.sectionBorrowers',
            },
            {
              id: 'borrowing-entities',
              label: 'navigation.borrowingEntities',
              path: '/control-center/entities',
              icon: 'AccountBalance',
              section: 'navigation.sectionBorrowers',
            },
            {
              id: 'guarantors',
              label: 'navigation.guarantors',
              path: '/loans/guarantors',
              icon: 'Shield',
              section: 'navigation.sectionBorrowers',
            },
            // Partnerships group — external b2b relationships.
            {
              id: 'partners',
              label: 'navigation.partners',
              path: '/partners',
              icon: 'Handshake',
              section: 'navigation.sectionPartnerships',
            },
            {
              id: 'partner-inquiries',
              label: 'navigation.partnerApplications',
              path: '/partners/inquiries',
              icon: 'Inbox',
              section: 'navigation.sectionPartnerships',
            },
            {
              id: 'referrals',
              label: 'navigation.referrals',
              path: '/partnerships/referrals',
              icon: 'Send',
              visibility: { requireAnyCapability: ['canBroker', 'canReceiveReferrals'] },
              section: 'navigation.sectionPartnerships',
            },
            {
              id: 'agreements',
              label: 'navigation.agreements',
              path: '/partnerships/agreements',
              icon: 'Gavel',
              visibility: { requireAnyCapability: ['canBroker', 'canReceiveReferrals'] },
              section: 'navigation.sectionPartnerships',
            },
            {
              id: 'commissions',
              label: 'navigation.commissions',
              path: '/partnerships/commissions',
              icon: 'Paid',
              visibility: { requireAnyCapability: ['canBroker', 'canReceiveReferrals'] },
              section: 'navigation.sectionPartnerships',
            },
          ],
        },
      ],
    },

    // ── Prospects: Sales pipeline ──
    {
      id: 'prospecting',
      label: 'navigation.prospecting',
      icon: 'Radar',
      items: [
        {
          id: 'prospecting',
          label: 'navigation.prospecting',
          path: '/prospecting',
          icon: 'Radar',
          keywords: ['leads', 'discovery', 'crm', 'sales', 'prospects', 'email', 'voice', 'outbound', 'campaigns'],
          tabs: [
            // Pipeline group — building the prospect list.
            {
              id: 'discovery',
              label: 'navigation.discovery',
              path: '/prospecting/discovery',
              icon: 'PersonSearch',
              section: 'navigation.sectionPipeline',
            },
            {
              id: 'leads',
              label: 'navigation.leadsList',
              path: '/prospecting/leads',
              icon: 'TableRows',
              section: 'navigation.sectionPipeline',
            },
            // Outreach group — channels for engaging prospects.
            {
              id: 'sequences',
              label: 'navigation.sequences',
              path: '/prospecting/sequences',
              icon: 'PlaylistPlay',
              section: 'navigation.sectionOutreach',
            },
            {
              id: 'email',
              label: 'campaigns.email.title',
              path: '/prospecting/email',
              icon: 'Email',
              section: 'navigation.sectionOutreach',
            },
            {
              id: 'voice',
              label: 'campaigns.voice.title',
              path: '/prospecting/voice',
              icon: 'Phone',
              section: 'navigation.sectionOutreach',
            },
          ],
        },
      ],
    },

    // ── Loans: Core business (Borrowers/Guarantors moved to Contacts) ──
    {
      id: 'loans',
      label: 'navigation.loans',
      icon: 'FolderShared',
      items: [
        {
          id: 'loans',
          label: 'navigation.loans',
          path: '/loans',
          icon: 'FolderShared',
          keywords: ['cases', 'term sheets', 'loans', 'applications', 'underwriting'],
          tabs: [
            {
              id: 'applications',
              label: 'navigation.applications',
              path: '/loans/applications',
              icon: 'Inbox',
              visibility: { requirePermissionString: 'consumer:pii_access' },
            },
            { id: 'cases', label: 'navigation.cases', path: '/loans', icon: 'FolderOpen' },
            { id: 'term-sheets', label: 'navigation.termSheets', path: '/loans/term-sheets', icon: 'Assignment' },
          ],
        },
      ],
    },

    // ── Finance: Operational Money Flows ──
    // Payments, Funds (disbursement payouts), and Offerings are real
    // transactional surfaces, not analytics — they manage money movement.
    // Gated by `billing.read` (see route-permissions.ts). Kept separate from
    // Reports so the rail signal is clear: this group is for *doing* money
    // operations, Reports is for *reading* metrics about them.
    {
      id: 'finance',
      label: 'navigation.finance',
      icon: 'AccountBalance',
      items: [
        {
          id: 'finance',
          label: 'navigation.finance',
          path: '/payments',
          icon: 'Paid',
          keywords: ['finance', 'payments', 'billing', 'funds', 'capital', 'offerings', 'investments', 'payouts'],
          tabs: [
            { id: 'payments', label: 'navigation.payments', path: '/payments', icon: 'Payment', badge: 'comingSoon' },
            { id: 'funds', label: 'navigation.funds', path: '/funds', icon: 'AccountBalance', badge: 'comingSoon' },
            {
              id: 'offerings',
              label: 'navigation.offerings',
              path: '/offerings',
              icon: 'Storefront',
              badge: 'comingSoon',
            },
          ],
        },
      ],
    },

    // ── Reports: Analytics + Historical/Audit Surfaces ──
    // Pipeline Analytics, Loan Portfolio, Financial dashboards, plus the
    // E-Signature History / Retained Copies / Document Approvals surfaces
    // that moved here from the legacy /documents top-level. Gated by
    // `analytics.read`. Operational money modules (payments / funds /
    // offerings) live in the Finance group above.
    {
      id: 'reports',
      label: 'navigation.reports',
      icon: 'BarChart',
      items: [
        {
          id: 'reports',
          label: 'navigation.reports',
          path: '/reports',
          icon: 'BarChart',
          keywords: [
            'reports',
            'analytics',
            'metrics',
            'esign',
            'e-signature',
            'signing',
            'retained',
            'copies',
            'approvals',
            'compliance',
            'audit',
          ],
          tabs: [
            // Analytics group — KPI / performance dashboards.
            {
              id: 'scorecard',
              label: 'navigation.scorecard',
              path: '/reports/scorecard',
              icon: 'Scoreboard',
              lock: { anyPermission: [Permission.scorecard.read, Permission.partners.read] },
              section: 'navigation.sectionAnalytics',
            },
            {
              id: 'pipeline-analytics',
              label: 'navigation.pipelineAnalytics',
              path: '/reports',
              icon: 'ShowChart',
              badge: 'comingSoon',
              section: 'navigation.sectionAnalytics',
            },
            {
              id: 'loan-portfolio',
              label: 'navigation.loanPortfolio',
              path: '/reports/loan-portfolio',
              icon: 'PieChart',
              badge: 'comingSoon',
              section: 'navigation.sectionAnalytics',
            },
            {
              id: 'financial',
              label: 'navigation.financial',
              path: '/reports/financial',
              icon: 'AttachMoney',
              badge: 'comingSoon',
              section: 'navigation.sectionAnalytics',
            },
            // Audit Trails group — historical/compliance surfaces from the
            // old Documents section.
            {
              id: 'esignature-history',
              label: 'navigation.eSignatureHistory',
              path: '/reports/e-signatures',
              icon: 'Draw',
              section: 'navigation.sectionAuditTrails',
            },
            {
              id: 'retained-copies',
              label: 'navigation.retainedCopies',
              path: '/reports/retained-copies',
              icon: 'FileCopy',
              section: 'navigation.sectionAuditTrails',
            },
            {
              id: 'document-approvals',
              label: 'navigation.documentApprovals',
              path: '/reports/document-approvals',
              icon: 'PlaylistAddCheck',
              section: 'navigation.sectionAuditTrails',
            },
          ],
        },
      ],
    },

    // ── Workspace: Team + Tools (consolidated) ──
    {
      id: 'workspace',
      label: 'navigation.workspace',
      icon: 'Workspaces',
      items: [
        {
          id: 'workspace',
          label: 'navigation.workspace',
          path: '/team/members',
          icon: 'Workspaces',
          keywords: ['team', 'members', 'tools', 'calculators', 'org chart'],
          tabs: [
            // Calculators is the utility surface — intentionally section-less so the
            // ungrouped-first sort rule pins it to the top of the panel (same
            // pattern as Control Center Overview).
            {
              id: 'calculators',
              label: 'navigation.calculators',
              path: '/tools/calculators',
              icon: 'Calculate',
            },
            // Team group — people and org-structure surfaces.
            {
              id: 'team-members',
              label: 'navigation.teamMembers',
              path: '/team/members',
              icon: 'Groups',
              lock: { anyPermission: [Permission.users.read] },
              section: 'navigation.sectionTeam',
            },
            {
              id: 'teams',
              label: 'navigation.teams',
              path: '/team/teams',
              icon: 'Group',
              lock: { anyPermission: [Permission.users.read, Permission.teams.manageMembers] },
              section: 'navigation.sectionTeam',
            },
            {
              id: 'org-chart',
              label: 'navigation.orgChart',
              path: '/team/org-chart',
              icon: 'Schema',
              lock: { anyPermission: [Permission.users.read] },
              section: 'navigation.sectionTeam',
            },
          ],
        },
      ],
    },

    // ── Settings: Control Center ──
    // All /control-center/* pages appear as tabs in the SidebarPanel when active.
    {
      id: 'settings',
      label: 'navigation.controlCenter',
      icon: 'SettingsInputComponent',
      items: [
        {
          id: 'control-center',
          label: 'navigation.controlCenter',
          path: '/control-center',
          icon: 'SettingsInputComponent',
          tabs: [
            // Hub overview — intentionally has NO `section` so it pins to the
            // top of the secondary panel via the ungrouped-first rule in
            // sortItemsByTranslatedLabel (the default landing page).
            { id: 'overview', label: 'common.overview', path: '/control-center', icon: 'Tune' },
            // Organization
            {
              id: 'branches',
              label: 'settings.branches',
              path: '/branches',
              icon: 'Business',
              section: 'settings.sectionOrganization',
            },
            {
              id: 'business-profile',
              label: 'settings.businessProfile',
              path: '/control-center/business/profile',
              icon: 'BusinessCenter',
              section: 'settings.sectionOrganization',
            },
            {
              id: 'permissions',
              label: 'settings.permissions',
              path: '/control-center/permissions',
              icon: 'Lock',
              lock: { anyPermission: [Permission.users.read] },
              section: 'settings.sectionOrganization',
            },
            {
              id: 'role-access',
              label: 'settings.roleAccess',
              path: '/control-center/role-access',
              icon: 'AdminPanelSettings',
              lock: { anyPermission: [Permission.users.read] },
              section: 'settings.sectionOrganization',
            },
            {
              id: 'team-settings',
              label: 'settings.teamSettings',
              path: '/control-center/team',
              icon: 'Groups',
              lock: { anyPermission: [Permission.users.read, Permission.teams.manageMembers] },
              section: 'settings.sectionOrganization',
            },
            // Audit logs — the /audit page existed since Issue #234 but was
            // never wired into the sidebar; admins could only reach it by
            // typing the URL. Placed in the Organization section alongside
            // Permissions / Role Access because audit history is governance.
            {
              id: 'audit',
              label: 'navigation.audit',
              path: '/audit',
              icon: 'History',
              lock: { anyPermission: [Permission.audit.read] },
              section: 'settings.sectionOrganization',
            },
            // Workflows & Pipelines
            {
              id: 'entity-definitions',
              label: 'settings.entityDefinitions',
              path: '/entities',
              icon: 'AccountBalance',
              section: 'settings.sectionWorkflows',
            },
            {
              id: 'business-onboarding',
              label: 'settings.businessOnboarding',
              path: '/control-center/business/onboarding',
              icon: 'RocketLaunch',
              section: 'settings.sectionWorkflows',
            },
            {
              id: 'onboarding',
              label: 'settings.onboarding',
              path: '/control-center/onboarding-wizard',
              icon: 'PlaylistAddCheck',
              section: 'settings.sectionWorkflows',
            },
            {
              id: 'pipelines',
              label: 'settings.pipelines',
              path: '/control-center/pipeline',
              icon: 'AccountTree',
              section: 'settings.sectionWorkflows',
            },
            {
              id: 'workflows',
              label: 'settings.workflows',
              path: '/control-center/workflows',
              icon: 'AutoFixHigh',
              lock: { anyPermission: [Permission.settings.update] },
              section: 'settings.sectionWorkflows',
            },
            // Loan Lifecycle Subsystem (Issue #1388 — strict isolation from Pipeline)
            {
              id: 'case-management',
              // Sidebar-specific label so this entry stops colliding with the
              // operational left-rail "Loans". Page header still renders
              // "Loans" via settings.caseManagement.
              label: 'settings.loanSettings',
              path: '/control-center/case-management',
              icon: 'FolderOpen',
              section: 'settings.sectionLoanLifecycle',
            },
            {
              id: 'loan-products',
              label: 'settings.loanProducts',
              path: '/control-center/loan-products',
              icon: 'Storefront',
              lock: { anyPermission: [Permission.settings.loanProducts.read] },
              section: 'settings.sectionLoanLifecycle',
            },
            {
              id: 'loan-stages',
              label: 'settings.loanStages',
              path: '/control-center/loan-stages',
              icon: 'Timeline',
              lock: { anyPermission: [Permission.settings.loanStages.read] },
              section: 'settings.sectionLoanLifecycle',
            },
            {
              id: 'loan-stage-requirements',
              label: 'settings.loanStageRequirements',
              path: '/control-center/loan-stage-requirements',
              icon: 'Checklist',
              lock: { anyPermission: [Permission.settings.loanStageRequirements.read] },
              section: 'settings.sectionLoanLifecycle',
            },
            {
              id: 'loan-data-points',
              label: 'settings.loanDataPoints',
              path: '/control-center/loan-data-points',
              icon: 'Schema',
              lock: { anyPermission: [Permission.settings.loanDataPoints.read] },
              section: 'settings.sectionLoanLifecycle',
            },
            {
              id: 'sizer-policy',
              label: 'settings.sizerPolicy',
              path: '/control-center/sizer-policy',
              icon: 'Calculate',
              lock: { anyPermission: [Permission.loanSizer.manageRules] },
              section: 'settings.sectionLoanLifecycle',
            },
            // Issue #1645 — Warehouse-line management Control Center
            // destinations. Live under the Loan Lifecycle section because
            // the credit-box / required-doc / profile editors are part of
            // the loan policy surface, not org settings.
            {
              id: 'warehouse-lines',
              label: 'controlCenter.warehouseLines.sidebarLabel',
              path: '/control-center/warehouse-lines',
              icon: 'AccountBalance',
              lock: { anyPermission: [Permission.settings.warehouseLines.read] },
              section: 'settings.sectionLoanLifecycle',
            },
            {
              id: 'warehouse-line-exceptions',
              label: 'controlCenter.warehouseLineExceptions.sidebarLabel',
              path: '/control-center/warehouse-line-exceptions',
              icon: 'GavelOutlined',
              lock: { anyPermission: [Permission.warehouseLineExceptions.read] },
              section: 'settings.sectionLoanLifecycle',
            },
            // Templates
            {
              id: 'doc-templates',
              label: 'settings.documentTemplates',
              path: '/control-center/document-templates',
              icon: 'FileCopy',
              section: 'settings.sectionTemplates',
            },
            {
              id: 'intake-templates',
              label: 'settings.intakeTemplates',
              path: '/control-center/intake-templates',
              icon: 'Assignment',
              section: 'settings.sectionTemplates',
            },
            {
              id: 'prospecting-templates',
              label: 'settings.prospectingTemplates',
              path: '/control-center/prospecting-templates',
              icon: 'ContentCopy',
              section: 'settings.sectionTemplates',
            },
            {
              id: 'pipeline-templates',
              label: 'settings.pipelineTemplates',
              path: '/control-center/pipeline-templates',
              icon: 'AccountTree',
              section: 'settings.sectionTemplates',
            },
            // Issue #1474 — Termsheet Templates merged into the unified
            // /control-center/document-templates page (filtered by ?type=termsheet).
            // Astor AI
            {
              id: 'astor-agents',
              label: 'settings.aiAgents',
              path: '/control-center/astor-agents',
              icon: 'AutoAwesome',
              section: 'settings.sectionAstorAI',
            },
            {
              id: 'astor-analytics',
              label: 'settings.astorAnalytics',
              path: '/control-center/astor/analytics',
              icon: 'Analytics',
              section: 'settings.sectionAstorAI',
            },
            {
              id: 'astor-autonomy',
              label: 'settings.aiAutonomy',
              path: '/control-center/astor/autonomy',
              icon: 'Psychology',
              section: 'settings.sectionAstorAI',
            },
            // UX v3 (#1739): platform-wide L1-L4 autonomy picker + per-agent
            // overrides. Distinct from the Issue #259 voice-campaign autonomy
            // above (which configures Retell escalation rules).
            {
              id: 'autonomy-levels',
              label: 'controlCenter.autonomy.title',
              path: '/control-center/autonomy',
              icon: 'Tune',
              section: 'settings.sectionAstorAI',
            },
            // Integrations — single connector hub. SFR Analytics, Slack,
            // Close, Zillow, and Experian all live here. The Close tile in
            // the connector list links to the per-tenant admin page at
            // /control-center/integrations/close.
            {
              id: 'integrations',
              label: 'settings.integrations',
              path: '/control-center/integrations',
              icon: 'Link',
              section: 'settings.sectionIntegrations',
            },
            {
              id: 'developer-api',
              label: 'settings.developerApi',
              path: '/control-center/developer-api',
              icon: 'Code',
              lock: { anyPermission: [Permission.apiKeys.read] },
              section: 'settings.sectionIntegrations',
            },
            {
              id: 'email-connections',
              label: 'settings.emailConnections',
              path: '/control-center/email-connections',
              icon: 'Email',
              section: 'settings.sectionIntegrations',
            },
            {
              id: 'webhooks',
              label: 'settings.webhooks',
              path: '/control-center/webhooks',
              icon: 'Webhook',
              lock: {
                anyPermission: [
                  Permission.settings.webhooks.create,
                  Permission.settings.webhooks.update,
                  Permission.settings.webhooks.delete,
                ],
              },
              section: 'settings.sectionIntegrations',
            },
            // Notifications & Finance
            {
              id: 'notifications',
              label: 'navigation.notifications',
              path: '/notifications',
              icon: 'Notifications',
              section: 'settings.sectionNotifications',
            },
            {
              id: 'commissions',
              label: 'navigation.commissions',
              path: '/control-center/commissions',
              icon: 'Paid',
              lock: {
                anyPermission: [Permission.settings.commissions.create, Permission.settings.commissions.update],
              },
              section: 'settings.sectionNotifications',
            },
            {
              id: 'pricing',
              label: 'settings.pricing',
              path: '/control-center/pricing',
              icon: 'AttachMoney',
              section: 'settings.sectionNotifications',
            },
            // Website
            {
              id: 'website-blog',
              label: 'website.blog.title',
              path: '/website/blog',
              icon: 'Article',
              section: 'settings.sectionWebsite',
            },
            {
              id: 'website-branding',
              label: 'website.branding.title',
              path: '/website/branding',
              icon: 'AutoFixHigh',
              section: 'settings.sectionWebsite',
            },
            {
              id: 'website-domain',
              label: 'customDomain.title',
              path: '/website/domain',
              icon: 'Language',
              section: 'settings.sectionWebsite',
            },
            {
              id: 'website-footer',
              label: 'website.footer.title',
              path: '/website/footer',
              icon: 'Assignment',
              section: 'settings.sectionWebsite',
            },
            {
              id: 'website-funded-deals',
              label: 'website.fundedDeals.title',
              path: '/website/funded-deals',
              icon: 'Paid',
              section: 'settings.sectionWebsite',
            },
            {
              id: 'website-navigation',
              label: 'website.navigation.title',
              path: '/website/navigation',
              icon: 'AccountTree',
              section: 'settings.sectionWebsite',
            },
            {
              id: 'website-pages',
              label: 'website.pages.title',
              path: '/website/pages',
              icon: 'FileCopy',
              section: 'settings.sectionWebsite',
            },
            {
              id: 'website-settings',
              label: 'website.settings.title',
              path: '/website/settings',
              icon: 'Language',
              section: 'settings.sectionWebsite',
            },
            {
              id: 'website-testimonials',
              label: 'website.testimonials.title',
              path: '/website/testimonials',
              icon: 'FormatQuote',
              section: 'settings.sectionWebsite',
            },
            // Personal
            {
              id: 'preferences',
              label: 'settings.preferences',
              path: '/control-center/preferences',
              icon: 'Settings',
              section: 'settings.sectionPersonal',
            },
            {
              id: 'devices',
              label: 'settings.myDevices',
              path: '/control-center/devices',
              icon: 'Devices',
              section: 'settings.sectionPersonal',
            },
          ],
        },
      ],
    },
  ],

  settingsNav: undefined,
};

/**
 * Business Portal Settings Hub Configuration
 *
 * MIRRORS the secondary sidebar (BUSINESS_PORTAL_NAV_CONFIG settings tabs).
 * Each section here corresponds 1:1 with a `tab.section` group in the sidebar
 * and contains the same items in the same order. Do NOT add categories here
 * that are not also in the sidebar (or vice versa) — the overview is the
 * landing surface for the sidebar, so any drift confuses users about what
 * settings exist.
 *
 * Sections (matching sidebar `settings.section*` keys):
 * 1. Organization        — Branches, Business Profile, Permissions, Role Access, Team Settings
 * 2. Workflows & Pipelines — Entity Definitions, Business Setup, Getting Started, Pipelines, Workflows
 * 3. Loan Lifecycle      — Loan Settings, Loan Products, Loan Stages, Stage Requirements, Loan Data Points
 * 4. Templates           — Document Templates, Intake Templates, Prospecting Templates, Pipeline Templates
 * 5. Astor AI            — Agents, Analytics, Autonomy
 * 6. Integrations        — Connected Apps, Developer API, Email Connections, Webhooks
 * 7. Notifications & Finance — Notifications, Commissions, Pricing
 * 8. Website             — Blog, Branding, Custom Domain, Footer, Funded Deals, Navigation, Pages, Settings, Testimonials
 * 9. Personal            — Preferences, My Devices
 */
export const BUSINESS_PORTAL_SETTINGS_CONFIG: SettingsHubConfig = {
  backButton: {
    label: 'common.back',
    path: '/dashboard',
  },
  searchPlaceholder: 'settings.searchPlaceholder',

  sections: [
    // 1. Organization
    {
      id: 'organization',
      title: 'settings.sectionOrganization',
      description: 'settings.organizationDescription',
      categories: [
        {
          id: 'branches',
          title: 'settings.branches',
          description: 'settings.branchesDescription',
          icon: 'Business',
          path: '/branches',
        },
        {
          id: 'business-profile',
          title: 'settings.businessProfile',
          description: 'settings.businessProfileDescription',
          icon: 'BusinessCenter',
          path: '/control-center/business/profile',
        },
        {
          id: 'permissions',
          title: 'settings.permissions',
          description: 'settings.permissionsDescription',
          icon: 'Lock',
          path: '/control-center/permissions',
          lock: { anyPermission: [Permission.users.read] },
        },
        {
          id: 'role-access',
          title: 'settings.roleAccess',
          description: 'settings.roleAccessDescription',
          icon: 'AdminPanelSettings',
          path: '/control-center/role-access',
          lock: { anyPermission: [Permission.users.read] },
        },
        {
          id: 'team-settings',
          title: 'settings.teamSettings',
          description: 'settings.teamSettingsDescription',
          icon: 'Groups',
          path: '/control-center/team',
          lock: { anyPermission: [Permission.users.read, Permission.teams.manageMembers] },
        },
        {
          id: 'audit',
          title: 'navigation.audit',
          description: 'settings.auditLogsDescription',
          icon: 'History',
          path: '/audit',
          lock: { anyPermission: [Permission.audit.read] },
        },
      ],
    },
    // 2. Workflows & Pipelines
    {
      id: 'workflows-and-pipelines',
      title: 'settings.sectionWorkflows',
      description: 'settings.workflowsAndPipelinesDescription',
      categories: [
        {
          id: 'entity-definitions',
          title: 'settings.entityDefinitions',
          description: 'settings.entityDefinitionsDescription',
          icon: 'AccountBalance',
          path: '/entities',
        },
        {
          id: 'business-onboarding',
          title: 'settings.businessOnboarding',
          description: 'settings.businessOnboardingDescription',
          icon: 'RocketLaunch',
          path: '/control-center/business/onboarding',
        },
        {
          id: 'onboarding',
          title: 'settings.onboarding',
          description: 'settings.onboardingDescription',
          icon: 'PlaylistAddCheck',
          path: '/control-center/onboarding-wizard',
        },
        {
          id: 'pipelines',
          title: 'settings.pipelines',
          description: 'settings.pipelinesDescription',
          icon: 'AccountTree',
          path: '/control-center/pipeline',
          lock: {
            anyPermission: [
              Permission.settings.pipelines.create,
              Permission.settings.pipelines.update,
              Permission.settings.read,
            ],
          },
        },
        {
          id: 'workflows',
          title: 'settings.workflows',
          description: 'settings.workflowsDescription',
          icon: 'AutoFixHigh',
          path: '/control-center/workflows',
          lock: { anyPermission: [Permission.settings.update] },
        },
      ],
    },
    // 3. Loan Lifecycle
    {
      id: 'loan-lifecycle',
      title: 'settings.sectionLoanLifecycle',
      description: 'settings.loanLifecycleDescription',
      categories: [
        {
          id: 'case-management',
          title: 'settings.loanSettings',
          description: 'settings.loanSettingsDescription',
          icon: 'FolderOpen',
          path: '/control-center/case-management',
        },
        {
          id: 'loan-products',
          title: 'settings.loanProducts',
          description: 'settings.loanProductsDescription',
          icon: 'Storefront',
          path: '/control-center/loan-products',
          lock: { anyPermission: [Permission.settings.loanProducts.read] },
        },
        {
          id: 'loan-stages',
          title: 'settings.loanStages',
          description: 'settings.loanStagesDescription',
          icon: 'Timeline',
          path: '/control-center/loan-stages',
          lock: { anyPermission: [Permission.settings.loanStages.read] },
        },
        {
          id: 'loan-stage-requirements',
          title: 'settings.loanStageRequirements',
          description: 'settings.loanStageRequirementsDescription',
          icon: 'Checklist',
          path: '/control-center/loan-stage-requirements',
          lock: { anyPermission: [Permission.settings.loanStageRequirements.read] },
        },
        {
          id: 'loan-data-points',
          title: 'settings.loanDataPoints',
          description: 'settings.loanDataPointsDescription',
          icon: 'Schema',
          path: '/control-center/loan-data-points',
          lock: { anyPermission: [Permission.settings.loanDataPoints.read] },
        },
        {
          id: 'sizer-policy',
          title: 'settings.sizerPolicy',
          description: 'settings.sizerPolicyDescription',
          icon: 'Calculate',
          path: '/control-center/sizer-policy',
          lock: { anyPermission: [Permission.loanSizer.manageRules] },
        },
        // Issue #1645 — Warehouse-line management settings tiles.
        {
          id: 'warehouse-lines',
          title: 'controlCenter.warehouseLines.tileTitle',
          description: 'controlCenter.warehouseLines.tileDescription',
          icon: 'AccountBalance',
          path: '/control-center/warehouse-lines',
          lock: { anyPermission: [Permission.settings.warehouseLines.read] },
        },
        {
          id: 'warehouse-line-exceptions',
          title: 'controlCenter.warehouseLineExceptions.tileTitle',
          description: 'controlCenter.warehouseLineExceptions.tileDescription',
          icon: 'GavelOutlined',
          path: '/control-center/warehouse-line-exceptions',
          lock: { anyPermission: [Permission.warehouseLineExceptions.read] },
        },
      ],
    },
    // 4. Templates
    {
      id: 'templates',
      title: 'settings.sectionTemplates',
      description: 'settings.templatesDescription',
      categories: [
        {
          id: 'document-templates',
          title: 'settings.documentTemplates',
          description: 'settings.documentTemplatesDescription',
          icon: 'FileCopy',
          path: '/control-center/document-templates',
          lock: { anyPermission: [Permission.templates.read] },
        },
        {
          id: 'intake-templates',
          title: 'settings.intakeTemplates',
          description: 'settings.intakeTemplatesDescription',
          icon: 'Assignment',
          path: '/control-center/intake-templates',
          lock: { anyPermission: [Permission.templates.read] },
        },
        {
          id: 'prospecting-templates',
          title: 'settings.prospectingTemplates',
          description: 'settings.prospectingTemplatesDescription',
          icon: 'ContentCopy',
          path: '/control-center/prospecting-templates',
        },
        {
          id: 'pipeline-templates',
          title: 'settings.pipelineTemplates',
          description: 'settings.pipelineTemplatesDescription',
          icon: 'AccountTree',
          path: '/control-center/pipeline-templates',
        },
      ],
    },
    // 5. Astor AI
    {
      id: 'astor-ai',
      title: 'settings.sectionAstorAI',
      description: 'settings.astorAiDescription',
      categories: [
        {
          id: 'astor-agents',
          title: 'settings.aiAgents',
          description: 'settings.aiAgentsDescription',
          icon: 'AutoAwesome',
          path: '/control-center/astor-agents',
          lock: { anyPermission: [Permission.agents.read] },
        },
        {
          id: 'astor-analytics',
          title: 'settings.astorAnalytics',
          description: 'settings.astorAnalyticsDescription',
          icon: 'Analytics',
          path: '/control-center/astor/analytics',
        },
        {
          id: 'astor-autonomy',
          title: 'settings.aiAutonomy',
          description: 'settings.aiAutonomyDescription',
          icon: 'Psychology',
          path: '/control-center/astor/autonomy',
          lock: { anyPermission: [Permission.agents.read, Permission.agents.configure] },
        },
        // UX v3 (#1739): platform-wide L1-L4 autonomy picker + per-agent overrides
        {
          id: 'autonomy-levels',
          title: 'controlCenter.autonomy.title',
          description: 'controlCenter.autonomy.description',
          icon: 'Tune',
          path: '/control-center/autonomy',
          lock: { anyPermission: [Permission.agents.read, Permission.agents.configure] },
        },
      ],
    },
    // 6. Integrations
    {
      id: 'integrations',
      title: 'settings.sectionIntegrations',
      description: 'settings.integrationsDescription',
      categories: [
        {
          id: 'integrations',
          title: 'settings.connectedApps',
          description: 'settings.connectedAppsDescription',
          icon: 'Link',
          path: '/control-center/integrations',
          lock: { anyPermission: [Permission.integrations.read] },
        },
        {
          id: 'developer-api',
          title: 'settings.developerApi',
          description: 'settings.developerApiDescription',
          icon: 'Code',
          path: '/control-center/developer-api',
          lock: { anyPermission: [Permission.apiKeys.read] },
        },
        {
          id: 'email-connections',
          title: 'settings.emailConnections',
          description: 'settings.emailConnectionsDescription',
          icon: 'Email',
          path: '/control-center/email-connections',
        },
        {
          id: 'webhooks',
          title: 'settings.webhooks',
          description: 'settings.webhooksDescription',
          icon: 'Webhook',
          path: '/control-center/webhooks',
          lock: {
            anyPermission: [
              Permission.settings.webhooks.create,
              Permission.settings.webhooks.update,
              Permission.settings.webhooks.delete,
            ],
          },
        },
      ],
    },
    // 7. Notifications & Finance
    {
      id: 'notifications-and-finance',
      title: 'settings.sectionNotifications',
      description: 'settings.notificationsAndFinanceDescription',
      categories: [
        {
          id: 'notifications',
          title: 'navigation.notifications',
          description: 'settings.notificationsDescription',
          icon: 'Notifications',
          path: '/notifications',
        },
        {
          id: 'commissions',
          title: 'navigation.commissions',
          description: 'settings.commissionsDescription',
          icon: 'Paid',
          path: '/control-center/commissions',
          lock: {
            anyPermission: [Permission.settings.commissions.create, Permission.settings.commissions.update],
          },
        },
        {
          id: 'pricing',
          title: 'settings.pricing',
          description: 'settings.pricingDescription',
          icon: 'AttachMoney',
          path: '/control-center/pricing',
        },
      ],
    },
    // 8. Website (sidebar order: blog, branding, domain, footer, funded-deals, navigation, pages, settings, testimonials)
    {
      id: 'website',
      title: 'settings.sectionWebsite',
      description: 'settings.websiteDescription',
      categories: [
        {
          id: 'blog',
          title: 'website.blog.title',
          description: 'website.blog.description',
          icon: 'Article',
          path: '/website/blog',
        },
        {
          id: 'branding',
          title: 'website.branding.title',
          description: 'website.branding.description',
          icon: 'AutoFixHigh',
          path: '/website/branding',
        },
        {
          id: 'custom-domain',
          title: 'customDomain.title',
          description: 'customDomain.description',
          icon: 'Language',
          path: '/website/domain',
        },
        {
          id: 'footer',
          title: 'website.footer.title',
          description: 'website.footer.description',
          icon: 'Assignment',
          path: '/website/footer',
        },
        {
          id: 'funded-deals',
          title: 'website.fundedDeals.title',
          description: 'website.fundedDeals.description',
          icon: 'Paid',
          path: '/website/funded-deals',
        },
        {
          id: 'navigation',
          title: 'website.navigation.title',
          description: 'website.navigation.description',
          icon: 'AccountTree',
          path: '/website/navigation',
        },
        {
          id: 'pages',
          title: 'website.pages.title',
          description: 'website.pages.description',
          icon: 'FileCopy',
          path: '/website/pages',
        },
        {
          id: 'website-settings',
          title: 'website.settings.title',
          description: 'website.settings.description',
          icon: 'Language',
          path: '/website/settings',
        },
        {
          id: 'testimonials',
          title: 'website.testimonials.title',
          description: 'website.testimonials.description',
          icon: 'FormatQuote',
          path: '/website/testimonials',
        },
      ],
    },
    // 9. Personal
    {
      id: 'personal',
      title: 'settings.sectionPersonal',
      description: 'settings.personalDescription',
      categories: [
        {
          id: 'preferences',
          title: 'settings.preferences',
          description: 'settings.preferencesDescription',
          icon: 'Settings',
          path: '/control-center/preferences',
        },
        {
          id: 'my-devices',
          title: 'settings.myDevices',
          description: 'settings.myDevicesDescription',
          icon: 'Devices',
          path: '/control-center/devices',
        },
      ],
    },
  ],
};
